/**
 * Tipos de assinatura alinhados ao backend (CreateSubscriptionDto, SubscriptionResponseDto, enums).
 */

export enum SubscriptionStatusEnum {
  PENDING = 'PENDING',
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  EXPIRED = 'EXPIRED',
  CANCELLED = 'CANCELLED',
}

export enum SubscriptionCycleEnum {
  MONTHLY = 'MONTHLY',
  YEARLY = 'YEARLY',
}

export enum SubscriptionBillingTypeEnum {
  PIX = 'PIX',
  PIX_AUTOMATIC = 'PIX_AUTOMATIC',
  CREDIT_CARD = 'CREDIT_CARD',
}

/** QR PIX: GET /admin/subscription/pix-qrcode (ação «Gerar novo QR») ou evento WS `subscription:pix_qr_ready`. */
export interface SubscriptionPixQrCode {
  encodedImage?: string;
  payload?: string;
  expiresAt?: string | null;
}

export interface Subscription {
  id: string;
  /** Id da assinatura no Asaas (quando exposto pela API) */
  externalSubscriptionId?: string | null;
  status: SubscriptionStatusEnum;
  cycle: SubscriptionCycleEnum;
  value: number;
  nextDueDate: string | null;
  billingType: SubscriptionBillingTypeEnum;
  establishmentPaymentMethodId?: string | null;
  pendingPlanId?: string | null;
  pendingPlanEffectiveAt?: string | null;
  /** Forma de pagamento agendada na próxima renovação (mudança de plano). */
  pendingBillingType?: SubscriptionBillingTypeEnum | null;
  gracePeriodEndsAt?: string | null;
  accessUntil?: string | null;
  createdAt: string;
  /** Presente só na resposta do POST criar assinatura quando billingType = PIX */
  pixQrCode?: SubscriptionPixQrCode | null;
  /**
   * FLOW-115: `true` quando existe cobrança PIX em aberto e `GET …/pix-qrcode` pode devolver QR.
   * `false` quando não há fatura pendente (ex.: já pago). Omitido em respostas antigas.
   */
  canRefreshPixQrCode?: boolean;
}

export interface CreditCardInput {
  holderName: string;
  number: string;
  expiryMonth: string;
  expiryYear: string;
  ccv: string;
}

export interface CreditCardHolderInfoInput {
  name: string;
  email: string;
  cpfCnpj: string;
  postalCode: string;
  addressNumber: string;
  address?: string | null;
  addressComplement?: string | null;
  province?: string | null;
  city?: string | null;
  state?: string | null;
  phone?: string | null;
  mobilePhone?: string | null;
}

export interface CreateSubscriptionInput {
  planId?: string;
  value?: number;
  cycle?: SubscriptionCycleEnum;
  billingType: SubscriptionBillingTypeEnum;
  nextDueDate?: string;
  description?: string;
  creditCard?: CreditCardInput;
  creditCardHolderInfo?: CreditCardHolderInfoInput;
  creditCardToken?: string;
  saveCard?: boolean;
  useSavedCard?: boolean;
}

export interface ChangeSubscriptionInput {
  planId: string;
  billingType?: SubscriptionBillingTypeEnum;
  creditCard?: CreditCardInput;
  creditCardHolderInfo?: CreditCardHolderInfoInput;
  creditCardToken?: string;
  saveCard?: boolean;
  /** Quando informado, usa este cartão salvo na assinatura em vez do padrão ou de um novo. */
  establishmentPaymentMethodId?: string;
}

export interface SubscriptionPlanFromAPI {
  id: string;
  name: string;
  code: string;
  price: number;
  cycle: SubscriptionCycleEnum;
  description: string | null;
  features: string[];
  popular: boolean;
  sortOrder: number;
}

/** GET /subscription-plan — catálogo público (FLOW-113). */
export interface SubscriptionPlanCatalogFromAPI {
  trialDays: number;
  plans: SubscriptionPlanFromAPI[];
}

export interface PaymentMethodSummaryFromAPI {
  last4: string;
  brand: string;
}

export interface SubscriptionTransactionFromAPI {
  id: string;
  externalPaymentId: string;
  amount: number;
  status: string;
  eventType: string;
  createdAt: string;
  /** Origem Asaas (PIX, CREDIT_CARD, …); opcional em transações antigas. */
  billingType?: string | null;
  paymentMethodSummary?: PaymentMethodSummaryFromAPI | null;
}

export interface SubscriptionTransactionsMetaFromAPI {
  totalItems: number;
  itemCount: number;
  itemsPerPage: number;
  totalPages: number;
  currentPage: number;
}

export interface SubscriptionTransactionsPaginatedResponseFromAPI {
  items: SubscriptionTransactionFromAPI[];
  meta: SubscriptionTransactionsMetaFromAPI;
}
